import React from 'react';
import styled from 'styled-components';
import { useDispatch } from 'react-redux';

import AgentMenu, { AgentMenuContainer } from './AgentMenu';
import BackIcon from '@ad-scripts/presentation/components/lib/icons/chevron_left.jsx';
import InlineContainer from '@ad-scripts/presentation/components/common/containers/inline_container';
import Logo from '@ad-scripts/presentation/components/common/logo';
import QuestionMarkIcon from '@ad-scripts/presentation/components/lib/icons/question_mark_icon';
import SearchIcon from '@ad-scripts/presentation/components/lib/icons/search_icon';
import { GRAYS } from '@ad-scripts/presentation/components/common/colors';
import { toastInfo } from 'src/actions/toast';

type TopBarProps = {
  hideSearch: boolean;
  showSupportChat: boolean;
};

declare global {
  interface Window {
    GladlySupport?: {
      show: () => void;
    };
  }
}

const TopBar: React.FC<TopBarProps> = ({ hideSearch, showSupportChat }) => {
  const dispatch = useDispatch();

  const handleSupportClick = (): void => {
    if (window.GladlySupport) {
      window.GladlySupport.show();
    } else {
      dispatch(toastInfo('Please turn off your ad-blocker to use the Support Chat'));
    }
  };

  return (
    <TopBarContainer>
      <ContentSection>
        <LeftTopNavColumn>
          <NavMenuPlaceholderDiv />
          <StyledAnchor href="/home">
            <StyledBackIcon />
            {'Home'}
          </StyledAnchor>
        </LeftTopNavColumn>
        <CenterTopNavColumn>
          <a href="/home">
            <LogoContainer />
          </a>
        </CenterTopNavColumn>
        <RightTopNavColumn>
          {!hideSearch && (
            <StyledSearchLink href="/search">
              <StyledSearch />
            </StyledSearchLink>
          )}
          {showSupportChat && (
            <StyledSupportButton type="button" onClick={handleSupportClick}>
              <StyledQuestionMark />
            </StyledSupportButton>
          )}
          <AgentMenu />
        </RightTopNavColumn>
      </ContentSection>
    </TopBarContainer>
  );
};

const ContentSection = ({ children }) => <TopBarContent>{children}</TopBarContent>;

export default TopBar;

const NavMenuPlaceholderDiv = styled.div`
  width: 75px;
`;

const TopBarContainer = styled.div`
  background-color: ${p => p.theme.colors.white};
  box-shadow: ${p => p.theme.boxShadow.medium};
  height: 60px;
  position: sticky;
  top: 0;
  width: 100%;
  z-index: 1;

  display: block;

  ${NavMenuPlaceholderDiv} {
    display: none;
  }

  ${AgentMenuContainer} {
    margin-right: 10px;
  }
`;

const TopBarContent = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  max-width: 1496px;
  padding-left: 20px;
  height: 60px;
  margin: auto;
`;

const LeftTopNavColumn = styled.div`
  align-items: center;
  display: flex;
`;

const CenterTopNavColumn = styled.div`
  align-items: center;
  display: flex;
  justify-content: center;
`;

const RightTopNavColumn = styled(InlineContainer).attrs({ inset: 'none', inline: 'small' })`
  display: flex;
  justify-content: flex-end;
  justify-items: center;
  align-items: center;
`;

const LogoContainer = styled(Logo)`
  /* This style matches the Agent Desktop logo placement */
  height: 25px;
  margin-top: 5px;
  width: 98px;
`;

const StyledBackIcon = styled(BackIcon)`
  color: ${GRAYS.xdark}; /* DEPRECATED COLOR: Used until Agent Desktop back icon refactor */
  height: 21px;
  width: 20px;
  stroke-width: 0.85;
  margin-right: 3px;
`;

const StyledAnchor = styled.a`
  align-items: center;
  color: ${GRAYS.xdark}; /* DEPRECATED COLOR: Used until Agent Desktop back icon refactor */
  display: flex;
  text-decoration: none;
`;

const StyledSearch = styled(SearchIcon)`
  fill: ${p => p.theme.colors.black};
  height: 16px;
  &:hover {
    fill: ${p => p.theme.colors.green400};
  }
`;

const StyledSearchLink = styled.a`
  text-decoration: none;
  height: 32px;
  width: 32px;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover svg {
    fill: ${p => p.theme.colors.green400};
  }
`;

const StyledSupportButton = styled.button`
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  height: 32px;
  width: 32px;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover svg {
    color: ${p => p.theme.colors.green400};
  }
`;

const StyledQuestionMark = styled(QuestionMarkIcon)`
  color: ${p => p.theme.colors.black};
  height: 16px;
  width: 16px;
  stroke-width: 0.85;
  transition: color 0.2s ease;

  &:hover {
    color: ${p => p.theme.colors.green400};
  }
`;
